import { useEffect, useRef, type InputHTMLAttributes, type Ref } from 'react';
import { CloseCircle, SearchNormal1 } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { assignRef } from '../internal/refs';
import { useControllableState } from '../internal/useControllableState';
import { Spinner } from '../Spinner/Spinner';
import type { ControlSize } from '../Form/TextInput';
import formStyles from '../Form/Form.module.css';
import styles from './SearchInput.module.css';

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'value' | 'defaultValue' | 'onChange'
> {
  /** Accessible name (required: search boxes usually have no visible label). */
  label: string;
  value?: string;
  defaultValue?: string;
  /** Fires on every keystroke. */
  onValueChange?: (value: string) => void;
  /** Fires after the user stops typing for `debounceMs`, and immediately on clear/Enter. */
  onDebouncedChange?: (value: string) => void;
  debounceMs?: number;
  /** Shows a small spinner (e.g. while results load). */
  loading?: boolean;
  size?: ControlSize;
  clearLabel?: string;
  wrapperClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

export function SearchInput({
  label,
  value: valueProp,
  defaultValue = '',
  onValueChange,
  onDebouncedChange,
  debounceMs = 300,
  loading = false,
  size = 'md',
  clearLabel = 'Clear search',
  placeholder = 'Search',
  wrapperClassName,
  className,
  disabled,
  onKeyDown,
  ref,
  ...rest
}: SearchInputProps) {
  const [value, setValue] = useControllableState(valueProp, defaultValue, onValueChange);
  const timer = useRef<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function emitNow(next: string) {
    window.clearTimeout(timer.current);
    onDebouncedChange?.(next);
  }

  function schedule(next: string) {
    window.clearTimeout(timer.current);
    if (!onDebouncedChange) return;
    timer.current = window.setTimeout(() => onDebouncedChange(next), debounceMs);
  }

  return (
    <div
      className={cx(
        formStyles.control,
        formStyles[`control-${size}`],
        disabled && formStyles.controlDisabled,
        styles.root,
        wrapperClassName,
      )}
    >
      <span className={formStyles.adornment} aria-hidden="true">
        <SearchNormal1 size={size === 'sm' ? 16 : 18} color="currentColor" />
      </span>
      <input
        ref={(el) => {
          inputRef.current = el;
          assignRef(ref, el);
        }}
        type="search"
        aria-label={label}
        className={cx(formStyles.input, className)}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          setValue(event.target.value);
          schedule(event.target.value);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented) return;
          if (event.key === 'Enter') emitNow(value);
          if (event.key === 'Escape' && value) {
            event.preventDefault();
            event.stopPropagation();
            setValue('');
            emitNow('');
          }
        }}
        {...rest}
      />
      <span className={formStyles.trailing}>
        {loading && <Spinner size="sm" label="Searching" />}
        {value && !disabled && (
          <button
            type="button"
            className={formStyles.inlineButton}
            aria-label={clearLabel}
            onClick={() => {
              setValue('');
              emitNow('');
              inputRef.current?.focus();
            }}
          >
            <CloseCircle size={16} color="currentColor" aria-hidden="true" />
          </button>
        )}
      </span>
    </div>
  );
}
